// src/components/engine/IncidentReport.jsx вЂ” v2.2 / Task A2
//
// Evidence Layer вЂ” inline HTML report per incident.
// Triggered from DecisionTimeline expanded view: "View Incident Report" button.
// NO backend. NO PDF. NO new API endpoints.
// Data: uses existing event object from engine_events.
//
// Report shows:
//   1. Incident summary (risk_status, action, confidence, mode)
//   2. Root cause (derived from reasons[])
//   3. Timeline chain (narrative sequence)
//   4. Action taken
//   5. Prevented loss (if available)
//   6. "What happened vs what would have happened" (counterfactual narrative)

import { useState } from "react";
import LossAttributionBlock, { deriveAnomalyType } from "./LossAttributionBlock";

const MONO = "var(--font-mono)";

function fmtTs(ts) {
  if (!ts) return "вЂ”";
  return new Date(Number(ts) * 1000).toLocaleString("en-GB", {
    year: "numeric", month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

function confLabel(c) {
  if (c == null) return "вЂ”";
  const v = Number(c);
  return v >= 0.75 ? `${v.toFixed(2)} (HIGH)` : v >= 0.5 ? `${v.toFixed(2)} (MEDIUM)` : `${v.toFixed(2)} (LOW)`;
}

// в”Ђв”Ђ Narrative summary generator в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
// A2 requirement: 1-paragraph narrative summary
function buildNarrative(event) {
  const time    = fmtTs(event.timestamp);
  const anomaly = deriveAnomalyType(event.reasons ?? [], event.risk_status);
  const action  = event.action ?? "observe";
  const mode    = event.mode ?? "balanced";

  const causeMap = {
    attribution: "attribution data became delayed",
    conversion:  "conversion rate degraded below baseline",
    spend:       "traffic cost velocity exceeded expected pace",
    data:        "insufficient data points reduced system confidence",
    unknown:     "multiple signals showed abnormal behavior",
  };

  const actionMap = {
    reduce: "reduced spend to limit exposure",
    pause:  "paused campaigns to stop losses",
    alert:  "raised an alert for operator review",
    observe:"monitored campaign behavior",
    none:   "determined no action was required",
  };

  const cause_text  = causeMap[anomaly.type]  ?? "anomalous behavior was detected";
  const action_text = actionMap[action]       ?? action;
  const conf_num    = event.confidence != null ? `(confidence: ${Number(event.confidence).toFixed(2)})` : "";

  return `At ${time}, ${cause_text}. ` +
    `VIRAJ detected the signal and, ${action_text} ${conf_num}. ` +
    `The system's decision was based on: ${(event.reasons ?? ["signal anomaly"]).join("; ")}.`;
}

// в”Ђв”Ђ Counterfactual narrative в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
function buildCounterfactualText(event) {
  const action = event.action ?? "observe";
  const prevented = Number(event.prevented_loss ?? 0);

  if (action === "none" || action === "observe") {
    return "No action was taken — more data was needed before making a recommendation.";
  }
  if (prevented > 0) {
    return `Without automatic intervention, the trajectory suggests a projected loss of в‚¬${Math.round(prevented / 0.8).toLocaleString("en-US")}. ` +
      `VIRAJ acted and estimated в‚¬${Math.round(prevented).toLocaleString("en-US")} was prevented.`;
  }
  return "Without automatic intervention, the detected anomaly would have continued to compound.";
}

// в”Ђв”Ђ Report Panel в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
function ReportPanel({ event, onClose }) {
  const narrative       = buildNarrative(event);
  const counterfactual  = buildCounterfactualText(event);
  const prevented       = Number(event.prevented_loss ?? 0);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 500,
      background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "20px",
    }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        maxWidth: 640, width: "100%", maxHeight: "85vh", overflowY: "auto",
        background: "var(--bg-elevated)", border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
        boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: "1px solid var(--border-subtle)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          position: "sticky", top: 0, background: "var(--bg-elevated)", zIndex: 1,
        }}>
          <div>
            <div style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700,
                          color: "var(--text-primary)", letterSpacing: "0.06em" }}>
              Incident Report
            </div>
            <div style={{ fontFamily: MONO, fontSize: 9, color: "var(--text-muted)", marginTop: 2 }}>
              {fmtTs(event.timestamp)} В· {event.campaign_id ?? "вЂ”"}
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "transparent", border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-sm)", padding: "4px 10px",
            fontFamily: MONO, fontSize: 10, color: "var(--text-muted)",
            cursor: "pointer",
          }}>
            вњ• Close
          </button>
        </div>

        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 16 }}>

          {/* 1. Incident Summary */}
          <div>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
                          textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
              Incident Summary
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))", gap: 8 }}>
              {[
                { label: "Severity",   value: { critical:"Severe", high:"Significant", medium:"Moderate", low:"Minor" }[event.risk_status] ?? event.risk_status ?? "вЂ”",
                  color: event.risk_status === "critical" ? "var(--danger)" : event.risk_status === "high" ? "var(--danger)" : "var(--warning)" },
                { label: "Recommendation", value: { pause:"Pause campaign", reduce:"Reduce spend", alert:"Alert raised", observe:"Monitor closely" }[event.action] ?? (event.action ?? "вЂ”"), color: "var(--text-primary)" },
                { label: "Confidence", value: confLabel(event.confidence),         color: "var(--text-secondary)" },
                
              ].map(({ label, value, color }) => (
                <div key={label} style={{ padding: "8px 10px", borderRadius: "var(--radius-sm)",
                                          background: "var(--bg-card)", border: "1px solid var(--border-subtle)" }}>
                  <div style={{ fontFamily: MONO, fontSize: 8, color: "var(--text-muted)",
                                textTransform: "uppercase", marginBottom: 3 }}>{label}</div>
                  <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color }}>{value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Root Cause */}
          <div>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
                          textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
              Root Cause
            </div>
            <LossAttributionBlock reasons={event.reasons ?? []} risk_status={event.risk_status} />
          </div>

          {/* 3. Narrative summary */}
          <div style={{ padding: "14px 16px", borderRadius: "var(--radius)",
                        background: "var(--bg-card)", border: "1px solid var(--border-subtle)" }}>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
                          textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
              What happened
            </div>
            <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)",
                          lineHeight: 1.65 }}>
              {narrative}
            </div>
          </div>

          {/* 4. Signals / reasons */}
          {event.reasons?.length > 0 && (
            <div>
              <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
                            textTransform: "uppercase", color: "var(--text-muted)", marginBottom: 8 }}>
                Signals detected
              </div>
              {event.reasons.map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 7, marginBottom: 5 }}>
                  <span style={{ color: "var(--danger)", flexShrink: 0 }}>в†“</span>
                  <span style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.5 }}>{r}</span>
                </div>
              ))}
            </div>
          )}

          {/* 5. Counterfactual */}
          <div style={{ padding: "14px 16px", borderRadius: "var(--radius)",
                        background: "rgba(18,204,118,0.05)", border: "1px solid rgba(18,204,118,0.18)" }}>
            <div style={{ fontFamily: MONO, fontSize: 9, letterSpacing: "0.14em",
                          textTransform: "uppercase", color: "var(--success)", marginBottom: 8, opacity: 0.8 }}>
              What would have happened without protection
            </div>
            <div style={{ fontFamily: MONO, fontSize: 11, color: "var(--text-secondary)", lineHeight: 1.65 }}>
              {counterfactual}
            </div>
            {prevented > 0 && (
              <div style={{ marginTop: 10, fontFamily: MONO, fontSize: 16, fontWeight: 700,
                            color: "var(--success)" }}>
                в‚¬{Math.round(prevented).toLocaleString("en-US")} prevented
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

// в”Ђв”Ђ Trigger button в”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђв”Ђ
/**
 * Small "View Incident Report" button вЂ” embed in DecisionTimeline expanded body.
 * @param {{ event: object }} props
 */
export default function IncidentReportTrigger({ event }) {
  const [open, setOpen] = useState(false);

  // Only show for decision/action events with real data
  const type = event?.event_type ?? "decision";
  if (type !== "decision" && type !== "action") return null;
  if (!event?.risk_status || event.risk_status === "low") return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5,
          padding: "4px 10px", borderRadius: "var(--radius-sm)",
          background: "var(--accent-faint)", border: "1px solid var(--border-accent)",
          fontFamily: MONO, fontSize: 9, fontWeight: 700,
          letterSpacing: "0.1em", color: "var(--accent-primary)",
          cursor: "pointer", textTransform: "uppercase",
          marginTop: 6,
        }}
      >
        в—‰ View Incident Report
      </button>
      {open && <ReportPanel event={event} onClose={() => setOpen(false)} />}
    </>
  );
}


